// @ts-strict
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AppConstants } from "@app/utils";
import { LMCreateReportDialogService, LMReportPersonalizationDialogComponent } from "@app/components";
import { LMReportsCatalogStateService } from "@states/services/lm-reports-catalog-state.service";
import { SohoAlertDirective } from "ids-enterprise-ng";
import { LMContextService, LMFormatUtilService, LMUrlUtilService } from "@app/services";
import { BehaviorSubject, Observable, Subject, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { HttpClient } from "@angular/common/http";

@Component({
  selector: 'lm-list-personalization-display',
  standalone: false,
  templateUrl: './lm-list-personalization-display.component.html',
  styleUrls: [ 'lm-list-personalization-display.component.scss' ],
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush
  })

export class LMListPersonalizationDisplayComponent {
  private numberOfColumnsSubject = new BehaviorSubject<number>(this.model?.numberOfColumns || 0);
  numberOfColumns$ = this.numberOfColumnsSubject.asObservable();

  get model(): LMListPersonalizationInfo  {
    return this._model;
  }

  @Input() set model(model: LMListPersonalizationInfo) {
    const oldNumberOfColumns = this._model?.numberOfColumns;
    this._model = model;

    if (!this._model.paperSize) {
      this._model.paperSize = AppConstants.DEFAULT_PAPER_SIZE.value;
    }

    if (oldNumberOfColumns !== this._model.numberOfColumns) {
      // Force the spinbox to fully re-render.
      // soho-spinbox component fails to update its attributes once they are initially set.
      this.numberOfColumnsSubject.next(0);
      this.changeDetectorRef.detectChanges();

      this.numberOfColumnsSubject.next(this._model.numberOfColumns);
    }

    this.initialize();
  }

  @Input() appReport!: LMAppReport;

  reportCatalogEnabled!: boolean;

  pdfPaperSizeDropdownAttributes = [ { name: 'data-automation-id', value: 'lm-personalization-pdf-paper-size' } ];
  pdfFontOffsetOptionDropdownAttributes = [ { name: 'data-automation-id', value: 'lm-personalization-pdf-font-offset-option' } ];
  retentionDurationOptionDropdownAttributes = [ { name: 'data-automation-id', value: 'lm-personalization-retention-duration-option' } ];
  pageSizeOptionDropdownAttributes = [ { name: 'data-automation-id', value: 'lm-personalization-page-size-option' } ];
  customFontOffsetSpinboxAttributes = [ { name: 'data-automation-id', value: 'lm-personalization-pdf-font-offset' } ];
  customFontOffsetSpinBoxDataOptions = JSON.stringify({ min: -5, max: 20, attributes: this.customFontOffsetSpinboxAttributes });
    retentionDurationSpinBoxAttributes = [ { name: 'data-automation-id', value: 'lm-personalization-retention-duration-font-offset' } ];
    retentionDurationSpinBoxDataOptions = JSON.stringify({ min: 1, max: 9999, step: 1, attributes: this.retentionDurationSpinBoxAttributes });
  categoryDropdownAttributes = [ { name: 'data-automation-id', value: 'lm-personalization-app-report-module' } ];
  subCategoryDropdownAttributes = [ { name: 'data-automation-id', value: 'lm-personalization-app-report-category' } ];
  viewNameDropdownAttributes = [ { name: 'data-automation-id', value: 'lm-personalization-view-name' } ];
  displaySearchDropdownAttributes = [ { name: 'data-automation-id', value: 'lm-personalization-display-search' } ];
  pageSizes: number[] = [];
  viewNames!: any[];
  private static readonly generalCategory = { value: '', label: 'General', selected: true };
  private _model!: LMListPersonalizationInfo;
  paperSizeList : Observable<Array<LMPdfPageSize>>;
  columnsToFreezeSpinboxAttributes = [ { name: 'data-automation-id', value: 'lm-personalization-columns-to-freeze' } ];
  columnsToFreezeSpinBoxDataOptions : any;

  constructor(
      private lmContextService: LMContextService,
      private lmReportsCatalogStateService: LMReportsCatalogStateService,
      private lmFormatUtilService: LMFormatUtilService,
      private lmCreateReportDialogService: LMCreateReportDialogService,
      private lmUrlUtilService: LMUrlUtilService,
      private http: HttpClient,
      private changeDetectorRef: ChangeDetectorRef
  ) {
    this.reportCatalogEnabled = this.lmContextService.getContext().enableReportCatalog || false;
    this.paperSizeList = this.getPdfSizeList();
    this.registerCustomRules();
  }

  registerCustomRules(){
    const invalidPageSize: any = {
      check: (value: any, field: any, grid: any) => this.validateCustomSize(value),
      id: 'custom',
      type: 'error',
      message: Soho.Locale.translate('InvalidSize')
    };

    // @ts-ignore
    Soho.Validation.rules['invalidPageSize'] = invalidPageSize;
  }

  validateCustomSize(value: any) {
    if (!value?.trim()) {
      return true;
    }
    const pattern = new RegExp('^\\d{1,5}(\\.\\d{1,2})?$');
    return pattern.test(value);
  }

  onFontOffsetOptionChange(event: any) {
    const offsetOption = event?.['data'];
    this.updatePdfFontOffsetFromOption(offsetOption);
  }

  onRetentionDurationOptionChange(event: any) {
    const option = event?.['data'];
    this.updateRetentionDurationFromOption(option);
  }

  private initialize() {
    this.pageSizes = this.getPageSizes(this.model.showLines);
    this.initializeColumnsToFreeze();
    this.updateColumnsConfiguration();
  }

  private initializeColumnsToFreeze(): void {
    if (!this.model.columnsToFreeze || this.model.columnsToFreeze < 1) {
      this.model.columnsToFreeze = 1;
    }
  }

  private updateColumnsConfiguration(): void {
    const maxColumns = this.model.numberOfColumns || 1;

    this.columnsToFreezeSpinBoxDataOptions = JSON.stringify({
      min: 1,
      attributes: this.columnsToFreezeSpinboxAttributes
    });

    // Ensure columnsToFreeze doesn't exceed the maximum
    // @ts-ignore
    if (this.model.columnsToFreeze > maxColumns) {
      this.model.columnsToFreeze = maxColumns;
    }
  }

  private updatePdfFontOffsetFromOption(offsetOption: string) {
    if (offsetOption === 'default') {
      this.model.fontOffset = 0;
    } else if (offsetOption === 'small' ) {
      this.model.fontOffset = -2;
    } else if (offsetOption === 'smaller' ) {
      this.model.fontOffset = -3;
    } else if (offsetOption === 'smallest') {
      this.model.fontOffset = -5;
    } else if (offsetOption !== 'custom') {
      this.model.fontOffset = 0;
    }
  }

  private updateRetentionDurationFromOption(option: string) {
    if (option === 'default') {
      this.model.retentionDuration = 0;
    } else if (option === 'one-week' ) {
      this.model.retentionDuration = 7;
    } else if (option === 'one-month' ) {
      this.model.retentionDuration = 30;
    } else if (option === 'one-year') {
      this.model.retentionDuration = 365;
    } else if (option === 'five-years') {
      this.model.retentionDuration = 1825;
    } else if (option !== 'custom') {
      this.model.retentionDuration = 0;
    }
  }

  private getPageSizes(defaultPageSize?:  number): number[] {
    if (this.model.listType === 'tree' || this.model.listType === 'cube')
      return [];

    let pageSizeArray = AppConstants.PAGE_SIZES;
    if (defaultPageSize) {
      if (!pageSizeArray.includes(defaultPageSize)) {
        pageSizeArray = pageSizeArray.concat(defaultPageSize).sort((a, b) => a - b);
      }
    }

    return pageSizeArray;
  }

  viewNameSource = (response: any, searchTerm: any) => {
    response(this.viewNames, true);
  };

  onModuleChanged() {
    const reportState = this.lmReportsCatalogStateService.getCatalogReportState();

    this.model.category = ((reportState.categoriesByModule?.[this.model.module] || []) as LMAppReportOption[])?.find(cat => cat.selected)?.value || '';

    if (Object.keys(reportState.categoriesByModule || {}).length) {
      this.appReport.categories = [ LMListPersonalizationDisplayComponent.generalCategory, ...((reportState.categoriesByModule?.[this.model.module] || []) as LMAppReportOption[]) ];
      return;
    }

    this.lmCreateReportDialogService.getCategoriesByModule(this.model.businessClass, this.model.listName)
        .pipe(LMCreateReportDialogService.pipeOperatorHelper(this))
        .subscribe((res) => {
          if (res) {
            const categories = [ LMListPersonalizationDisplayComponent.generalCategory, ...(res.categoriesByModule?.[this.model.module] || []) ];
            this.appReport.categories = this.lmFormatUtilService.getUniqueListByKey(categories, 'value');

            reportState.categoriesByModule = res.categoriesByModule;
            this.lmReportsCatalogStateService.saveCatalogReportState(reportState);
          }
        });
  }

  onModuleSelect = (response: SohoDropDownResponseFunction) => {
    const reportState = this.lmReportsCatalogStateService.getCatalogReportState();

    if (reportState.modules?.length) {
      response(reportState.modules);
      return;
    }

    this.lmCreateReportDialogService.getModules(this.model.businessClass, this.model.listName)
        // .pipe(LMCreateReportDialogService.pipeOperatorHelper(this))
        .subscribe((res) => {
          // if (!res) {
          //   this.addTimeoutInlineAlert('lm-personalization-app-report-module');
          //   // fallback to previous value
          //   response(this.appReport.modules);
          // }

          if (res?.modules) {
            const modules = res.modules;
            response(modules);
            reportState.modules = modules;
            this.lmReportsCatalogStateService.saveCatalogReportState(reportState);
          }
        });
  };

  onCategorySelect = (response: SohoDropDownResponseFunction) => {
    const reportState = this.lmReportsCatalogStateService.getCatalogReportState();

    if (Object.keys(reportState.categoriesByModule || {}).length) {
      const categories = [ LMListPersonalizationDisplayComponent.generalCategory, ...(reportState.categoriesByModule?.[this.model.module] || []) ];
      response(categories);
      return;
    }

    this.lmCreateReportDialogService.getCategoriesByModule(this.model.businessClass, this.model.listName)
        // .pipe(LMCreateReportDialogService.pipeOperatorHelper(this))
        .subscribe((res) => {
          // if (!res) {
          //   // this.addTimeoutInlineAlert('lm-personalization-app-report-category');
          //   // fallback to previous value
          //   response(this.appReport.categories);
          // }

          if (res) {
            let categories = [ LMListPersonalizationDisplayComponent.generalCategory, ...(res.categoriesByModule?.[this.model.module] || []) ];
            categories = this.lmFormatUtilService.getUniqueListByKey(categories, 'value');
            
            if (this.model.category) {
              const selectedCategory: LMAppReportOption = categories.find(category => category.label === this.model.category.replace(/_/g, ' '));
              if (selectedCategory) {
                selectedCategory.selected = true;
              }
            }

            response(categories);

            reportState.categoriesByModule = res.categoriesByModule;
            this.lmReportsCatalogStateService.saveCatalogReportState(reportState);
          }
        });
  };

  private getPdfSizeList(): Observable<Array<LMPdfPageSize>> {
    const pdfPageSizeURL = `${this.lmUrlUtilService.getBaseUrl()}`
        + '/list/PDFPageSize.PDFPageSizeSelectList?pageop=load&pagesize=-1';

    return this.http
        .get(pdfPageSizeURL).pipe(
            map((response: any) => {
              const paperSizesList = this.getPdfSizeData(response.dataViewSet);
              if (paperSizesList && paperSizesList.length > 0) {
                this.model.paperSize = this.model.paperSize || paperSizesList[0].value;
              }
              return paperSizesList;
            }),
            catchError(error => this.handleError(error)));
  }

  private getPdfSizeData(dataViewSet: LMDataViewSet): Array<LMPdfPageSize>{
    const pageSizes: Array<LMPdfPageSize> = [];
    if (dataViewSet && dataViewSet?.data && dataViewSet?.data.length > 0) {
      dataViewSet.data.forEach(pdfSize => {
        pageSizes.push({ value: pdfSize.fields['PDFPageSize'].value as string, description: pdfSize.representativeText });
      });
    } else {
      // Add default Letter size if no records returned
      pageSizes.push(AppConstants.DEFAULT_PAPER_SIZE);
    }
    return pageSizes;
  }

  private handleError(error: Response) {
    return throwError(error || 'Server error');
  }

  // private addTimeoutInlineAlert(id: string): void {
  //   // const timeoutAlert = this.alerts.find((alert: SohoAlertDirective) => (alert.elementRef.nativeElement as Element).getAttribute('id') === id);
  //   // timeoutAlert.addInlineMessage(Soho.Locale.translate('ListPersonalizationHTTPCallTimeout'), 'alert', true);
  // }

}

